import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import { Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import FocusedFlowPage from "components/FocusedFlowPage";
import useOptions from "hooks/useOptions";
import { createCollectionKit, validateCollectionKit } from "../../../utils/api";
import { BIOBANK_ROUTES } from "../../../constants/routes";

const TAMU_MAROON = "#500000";

const KitNumber = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || BIOBANK_ROUTES.COLLECTION_KITS;
  const [disabled, setDisabled] = useState(true);
  const [kitType, setKitType] = useState("");
  const [kitNumber, setKitNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isValidKit, setIsValidKit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const {
    options: kitTypeOptions,
    loading: kitTypeOptionsLoading,
    error: kitTypeOptionsError,
  } = useOptions("kit-types");

  const selectedKitTypeLabel =
    kitTypeOptions.find((option) => option.value === kitType)?.label || kitType;
  const batchId = `${kitType}${kitNumber}`;

  const handleBack = () => {
    navigate(returnTo);
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      await createCollectionKit(kitType, kitNumber);

      navigate(returnTo, {
        state: { complete: true, kitId: batchId },
      });
    } catch (error) {
      alert(`Failed to register kit: ${error.message}`);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const validateKitNumber = async (kitId) => {
    try {
      setValidating(true);
      setValidationError("");

      const existingKit = await validateCollectionKit(kitId);
      if (existingKit) {
        setValidationError("Kit number already exists in system");
        setIsValidKit(false);
        setDisabled(true);
        return;
      }

      setIsValidKit(true);
      setDisabled(false);
    } catch (error) {
      setValidationError("Error validating kit number");
      setIsValidKit(false);
      setDisabled(true);
    } finally {
      setValidating(false);
    }
  };

  const handleKitTypeChange = (event) => {
    const nextKitType = event.target.value;
    setKitType(nextKitType);
    setValidationError("");
    setIsValidKit(false);
    setDisabled(true);

    if (nextKitType && kitNumber.length === 5) {
      validateKitNumber(`${nextKitType}${kitNumber}`);
    }
  };

  const handleKitChange = (event) => {
    const value = event.target.value;
    const numericValue = value.replace(/\D/g, "");
    const limitedValue = numericValue.slice(0, 5);

    setKitNumber(limitedValue);
    setValidationError("");
    setIsValidKit(false);
    setDisabled(true);

    if (kitType && limitedValue.length === 5) {
      validateKitNumber(`${kitType}${limitedValue}`);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#FAFAFA",
      "& fieldset": {
        borderColor: "#D9D9D9",
      },
      "&:hover fieldset": {
        borderColor: TAMU_MAROON,
      },
      "&.Mui-focused fieldset": {
        borderColor: TAMU_MAROON,
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: TAMU_MAROON,
    },
  };

  return (
    <FocusedFlowPage
      title="Register Collection Batch"
      subtitle="Select the kit type based on the associated study, then enter the 5-digit kit number printed on the physical kit box or plastic bag (the number shown on the printed sticker). The system will register the canonical batch ID using the new collection-batch contract."
      onClose={() => navigate(returnTo)}
      maxWidth={640}
      actions={[
        <Button
          key="back"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          BACK
        </Button>,
        <Button
          key="complete"
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={
            disabled ||
            loading ||
            validating ||
            kitTypeOptionsLoading ||
            !!kitTypeOptionsError
          }
          sx={{ backgroundColor: TAMU_MAROON, "&:hover": { backgroundColor: "#3A0000" } }}
        >
          {loading
            ? "REGISTERING..."
            : validating
            ? "VALIDATING..."
            : "COMPLETE REGISTRATION"}
        </Button>,
      ]}
    >
      <Box
        sx={{
          width: "90%",
          mx: "auto",
          p: 3,
          borderRadius: 3,
          border: "1px solid #EAEAEA",
          borderTop: `4px solid ${TAMU_MAROON}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Kit Type"
            required
            select
            value={kitType}
            onChange={handleKitTypeChange}
            placeholder="Select the batch prefix"
            helperText={
              kitTypeOptionsError
                ? "Failed to load kit types."
                : kitTypeOptionsLoading
                ? "Loading kit types..."
                : kitTypeOptions.length === 0
                ? "No kit types are available."
                : "Choose the batch prefix"
            }
            fullWidth
            sx={{ flex: 1, ...fieldSx }}
            disabled={kitTypeOptionsLoading || !!kitTypeOptionsError}
          >
            {kitTypeOptionsLoading && (
              <MenuItem value="" disabled>
                Loading kit types...
              </MenuItem>
            )}
            {!kitTypeOptionsLoading && kitTypeOptions.length === 0 && (
              <MenuItem value="" disabled>
                No kit types available
              </MenuItem>
            )}
            {kitTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Kit Number"
            required
            autoFocus
            value={kitNumber}
            onChange={handleKitChange}
            error={!!validationError}
            placeholder="5 digit number"
            helperText={
              validationError ||
              (!kitType
                ? "Select a kit type first"
                : kitNumber.length < 5
                ? `Enter ${5 - kitNumber.length} more digits`
                : isValidKit
                ? "Batch ID is available"
                : "Enter a 5-digit kit number")
            }
            inputProps={{
              maxLength: 5,
              pattern: "[0-9]*",
              inputMode: "numeric",
            }}
            fullWidth
            sx={{ flex: 1, ...fieldSx }}
          />
        </Stack>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: TAMU_MAROON, fontWeight: 600 }}>
          Confirm Kit Registration
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#3E3E3E", mb: 2 }}>
            Please review the details below before registering this collection batch.
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575" }}>
                Kit Type
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {selectedKitTypeLabel || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575" }}>
                Kit Number
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>{kitNumber || "—"}</Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="caption" sx={{ color: "#757575" }}>
                Batch ID
              </Typography>
              <Typography sx={{ fontWeight: 700, color: TAMU_MAROON }}>
                {batchId}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={loading}
            sx={{ backgroundColor: TAMU_MAROON, "&:hover": { backgroundColor: "#3A0000" } }}
          >
            {loading ? "REGISTERING..." : "CONFIRM & REGISTER"}
          </Button>
        </DialogActions>
      </Dialog>
    </FocusedFlowPage>
  );
};

export default KitNumber;